import { useCallback } from 'react'
import {
  makeProductClickEvent,
  sendEvent,
} from 'src/utils/restructure/analytics'
import { getStandardObject } from 'src/utils/amplitude/getStandardObject'
import { buildProductSelectedObject } from 'src/utils/amplitude/buildProductSelectedObject'
import type { AmplitudeData } from 'src/components/restructure/product/ProductCardV3/ProductCardV3'
import { getInitialItem } from 'src/components/restructure/utils/get-initial-sku'
import { getProductQueryObject } from 'src/utils/amplitude/useProductObject'
import { dispatchAmplitudeEvent } from 'src/utils/amplitude'
import dynamicYieldEngagement from 'src/utils/dynamicYield/dynamicYieldEngagement'
import { OptionsMap } from 'src/components/search/SortV2/Sort'
import { useGlobalContext } from 'src/components/contexts/GlobalContext'
import type { ProductItem } from 'src/components/restructure/product/types/productItem'
import { isAlbumPage } from 'src/utils/pdp/isAlbumPage'

import { sendSearchClickEvent } from '../analytics/platform/intelligentSearch'

type ShippingBadgePromotion =
  | import('src/utils/shippingCampaign').ShippingBadgeKey
  | 'none'
type ProductLinkOptions = {
  index: number
  product: any
  currentSku: any
  slug: string
  shelfRef?: string
  shelfEngine?: string
  amplitudeData?: AmplitudeData
  type?: string
  title?: string
  id?: string
  shippingBadgePromotion?: ShippingBadgePromotion
  handleClick?: () => void
}

// Função auxiliar para Linx/DY
function handleLinxOrDYClick({ shelfEngine, product }: any) {
  if (shelfEngine === 'dy' && !!product?.slotId) {
    dynamicYieldEngagement([
      {
        type: 'SLOT_CLICK',
        slotId: product.slotId,
      },
    ])
  }
}

function handleSearchClick({ index, product }: any) {
  sendSearchClickEvent({
    name: 'search_select_item',
    params: {
      url: window.location.href,
      position: index,
      productId: product.productId,
    },
  })
}

function handleAmplitudeEvent({
  shelfRef,
  standardObject,
  productObject,
  term,
  title,
  type,
  getItemPosition,
  totalCountOfRegisters,
  page,
  sort,
  selectedFacets,
  base,
  currentSku,
  shippingBadgePromotion,
}: any) {
  const promotionPayload = {
    product_badge_promotion: shippingBadgePromotion ?? 'none',
  }

  dispatchAmplitudeEvent({
    eventName: 'Product Selected',
    eventData: {
      ...standardObject,
      'product details': [productObject],
      ...buildProductSelectedObject({
        listName: shelfRef ?? term ?? title ?? '',
        listType: `${type ?? 'página de lista de produto'}`,
        productPosition: getItemPosition(),
        productsCount: totalCountOfRegisters ?? 0,
        pageNumber: page,
        sortOrder: OptionsMap[sort as keyof typeof OptionsMap],
        facets: selectedFacets,
        base,
      }),
      'product is available': currentSku?.isAvailable,
      ...promotionPayload,
    },
  })
}

function handleLastUnitDYEvent({ currentSku }: { currentSku: ProductItem }) {
  const {
    sellerDefault: {
      sellerOffer: { lastUnits },
    },
    flags,
  } = currentSku

  const isNewProduct = flags?.some(
    (flag) => flag?.value?.toLocaleLowerCase() === 'novidade'
  )

  if (isNewProduct || !lastUnits) {
    return null
  }

  window?.DY?.API('event', {
    name: 'last units clicked',
  })
}

function persistLastClickedList(lastList?: string) {
  if (typeof lastList !== 'string' || lastList.trim() === '') {
    return
  }

  try {
    localStorage.setItem('lastClickedList', lastList)
  } catch (error) {
    console.error('Error persisting last clicked list:', error)
  }
}

export const useProductLink = ({
  index,
  product,
  currentSku,
  slug,
  shelfRef,
  shelfEngine,
  amplitudeData,
  type,
  title,
  shippingBadgePromotion,
  handleClick,
}: ProductLinkOptions) => {
  const { pageData } = useGlobalContext()
  const standardObject = getStandardObject(pageData)
  const {
    page,
    itemsPerPage,
    totalCountOfRegisters,
    sort,
    selectedFacets,
    term,
    base,
  } = amplitudeData ?? {}

  const productObject = getProductQueryObject(
    getInitialItem(product.items, slug)
  )

  const getItemPosition = useCallback(() => {
    const currentPosition = index + 1

    if (shelfRef || page === 1) {
      return currentPosition
    }

    if (!page || !itemsPerPage) {
      return currentPosition
    }

    return (page - 1) * itemsPerPage + currentPosition
  }, [index, shelfRef, page, itemsPerPage])

  const triggerProductEvents = useCallback(() => {
    if (product?.clickUrl || product?.slotId) {
      handleLinxOrDYClick({ shelfEngine, product })
    }

    const productClick = makeProductClickEvent(product, product.items[0], {
      listName: shelfRef ?? 'product card',
      dimension29: shelfEngine ?? '',
    })

    sendEvent(productClick)
    handleSearchClick({ index, product })
    handleAmplitudeEvent({
      product,
      shelfRef,
      shelfEngine,
      index,
      standardObject,
      productObject,
      term,
      title,
      type: type || pageData?.currentPageType,
      getItemPosition,
      totalCountOfRegisters,
      page,
      sort,
      selectedFacets,
      base,
      currentSku,
      shippingBadgePromotion,
    })
    handleLastUnitDYEvent({ currentSku })
  }, [
    product,
    shelfRef,
    shelfEngine,
    index,
    standardObject,
    productObject,
    term,
    title,
    type,
    pageData?.currentPageType,
    getItemPosition,
    totalCountOfRegisters,
    page,
    sort,
    selectedFacets,
    base,
    currentSku,
    shippingBadgePromotion,
  ])

  const onClick = useCallback(() => {
    handleClick?.()
    triggerProductEvents()
    persistLastClickedList(shelfRef)
  }, [handleClick, shelfRef, triggerProductEvents])

  if (currentSku?.isKit && !isAlbumPage(product.productClusters)) {
    return {
      to: `/${slug}/kit`,
      onClick,
      'data-testid': 'product-link',
    }
  }

  return {
    to: `/${slug}/p`,
    onClick,
    'data-testid': 'product-link',
  }
}
